import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Match } from '@/lib/types';
import { toast } from '@/hooks/use-toast';
import { RealtimeChannel } from '@supabase/supabase-js';

interface ScoreUpdate {
  matchId: string;
  homeTeam: string;
  awayTeam: string;
  homeScore: number;
  awayScore: number;
  previousHomeScore?: number;
  previousAwayScore?: number;
  status: string;
}

interface UseRealtimeMatchOptions {
  matchId?: string;
  onMatchUpdate?: (match: Partial<Match>) => void;
  onScoreChange?: (update: ScoreUpdate) => void;
  enabled?: boolean;
}

export function useRealtimeMatch({
  matchId,
  onMatchUpdate,
  onScoreChange,
  enabled = true,
}: UseRealtimeMatchOptions) {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const previousScoresRef = useRef<{ home: number | null; away: number | null }>({
    home: null,
    away: null,
  });

  const showScoreNotification = useCallback((update: ScoreUpdate) => {
    const { homeTeam, awayTeam, homeScore, awayScore, previousHomeScore, previousAwayScore, status } = update;
    
    // Final score notification for finished matches
    if (status === 'finished') {
      toast({
        title: '🏁 Full Time',
        description: `${homeTeam} ${homeScore} - ${awayScore} ${awayTeam}`,
        duration: 5000,
      });
      return;
    }
    
    // Determine who scored (for live matches)
    let scorerTeam = '';
    if (previousHomeScore !== undefined && homeScore > previousHomeScore) {
      scorerTeam = homeTeam;
    } else if (previousAwayScore !== undefined && awayScore > previousAwayScore) {
      scorerTeam = awayTeam;
    }

    if (scorerTeam) {
      toast({
        title: '⚽ GOAL!',
        description: `${scorerTeam} scores! ${homeTeam} ${homeScore} - ${awayScore} ${awayTeam}`,
        duration: 5000,
      });
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Create channel for match updates
    const filter = matchId 
      ? `id=eq.${matchId}` 
      : undefined;

    const channel = supabase
      .channel(`matches-realtime${matchId ? `-${matchId}` : ''}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'matches',
          filter,
        },
        (payload) => {
          const newData = payload.new as any;
          const oldData = payload.old as any;
          
          console.log('[Realtime] Match update received:', {
            matchId: newData.id,
            homeTeam: newData.home_team,
            awayTeam: newData.away_team,
            oldScore: `${oldData.home_score ?? '-'}-${oldData.away_score ?? '-'}`,
            newScore: `${newData.home_score ?? '-'}-${newData.away_score ?? '-'}`,
            status: newData.status,
          });

          // Check if score changed
          const scoreChanged = 
            (newData.home_score !== oldData.home_score) ||
            (newData.away_score !== oldData.away_score);

          const statusChanged = newData.status !== oldData.status;
          const justFinished = statusChanged && newData.status === 'finished' && oldData.status === 'live';
          const isLive = newData.status === 'live';

          // Call update callback
          if (onMatchUpdate) {
            onMatchUpdate({
              id: newData.id,
              home_score: newData.home_score,
              away_score: newData.away_score,
              status: newData.status,
              statistics: newData.statistics,
              updated_at: newData.updated_at,
            });
          }

          // Only show notifications for:
          // 1. Score changes during live matches
          // 2. Match just finished (once, as final score)
          const shouldNotify = (scoreChanged && isLive) || justFinished;

          if (shouldNotify) {
            const update: ScoreUpdate = {
              matchId: newData.id,
              homeTeam: newData.home_team,
              awayTeam: newData.away_team,
              homeScore: newData.home_score ?? 0,
              awayScore: newData.away_score ?? 0,
              previousHomeScore: oldData.home_score ?? undefined,
              previousAwayScore: oldData.away_score ?? undefined,
              status: newData.status,
            };

            showScoreNotification(update);

            if (onScoreChange) {
              onScoreChange(update);
            }
          }
        }
      )
      .subscribe(() => {
        // Subscription status logged only in development
      });

    channelRef.current = channel;

    return () => {
      console.log('[Realtime] Cleaning up subscription');
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [matchId, enabled, onMatchUpdate, onScoreChange, showScoreNotification]);

  return {
    channel: channelRef.current,
  };
}

// Hook for subscribing to all live match updates (for homepage)
export function useRealtimeLiveMatches(
  onAnyMatchUpdate?: (match: Partial<Match>) => void
) {
  return useRealtimeMatch({
    enabled: true,
    onMatchUpdate: onAnyMatchUpdate,
  });
}
